import React, { useMemo, useRef, useEffect } from 'react';

/**
 * Stepper horizontal da trilha de contratação.
 * Dados vêm apenas da API (props) — sem persistir no browser.
 */
export default function TrilhaStepperHeader({
  etapas = [],
  etapaAtualId = null,
  etapaSelecionadaId = null,
  onSelectEtapa = null,
  dense = false,
}) {
  const scrollerRef = useRef(null);

  const lista = useMemo(() => {
    return [...(etapas || [])].sort(
      (a, b) => Number(a.ordem ?? 0) - Number(b.ordem ?? 0)
    );
  }, [etapas]);

  const atualId = Number(etapaAtualId);
  const selId = etapaSelecionadaId != null ? Number(etapaSelecionadaId) : atualId;

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const node = el.querySelector('[data-step-atual="1"]') || el.querySelector('[data-step-sel="1"]');
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [atualId, selId, lista.length]);

  if (!lista.length) {
    return (
      <p className="mf-page__meta" style={{ margin: '0.5rem 0' }}>
        Nenhuma etapa configurada no fluxo.
      </p>
    );
  }

  return (
    <div className={`mf-trilha${dense ? ' mf-trilha--dense' : ''}`} ref={scrollerRef}>
      <ol className="mf-trilha__list">
        {lista.map((et, idx) => {
          const id = Number(et.id);
          const isAtual = id === atualId || !!et.ehAtual;
          const status = String(et.status || '').toUpperCase();
          const isConcluida =
            status === 'CONCLUIDO' ||
            (!isAtual &&
              lista.some((x) => Number(x.id) === atualId) &&
              Number(et.ordem) < Number(lista.find((x) => Number(x.id) === atualId)?.ordem));
          const isFutura = !isAtual && !isConcluida;
          const isSel = id === selId;

          let cls = 'mf-trilha__step';
          if (isConcluida) cls += ' mf-trilha__step--done';
          if (isAtual) cls += ' mf-trilha__step--atual';
          if (isFutura) cls += ' mf-trilha__step--future';
          if (isSel) cls += ' mf-trilha__step--sel';

          const clickable = isConcluida || isAtual;
          const title = isFutura
            ? 'Ainda não iniciada'
            : isConcluida
              ? 'Concluída — clique para ver resumo'
              : 'Etapa atual';

          return (
            <li key={et.id || idx} className={cls} data-step-atual={isAtual ? '1' : '0'} data-step-sel={isSel ? '1' : '0'}>
              {idx > 0 ? <span className="mf-trilha__line" aria-hidden /> : null}
              <button
                type="button"
                className="mf-trilha__btn"
                title={title}
                disabled={!clickable}
                onClick={() => {
                  if (!clickable || !onSelectEtapa) return;
                  onSelectEtapa(et);
                }}
              >
                <span className="mf-trilha__num">
                  {isConcluida ? '✓' : idx + 1}
                </span>
                <span className="mf-trilha__nome">{et.nome || `Etapa ${idx + 1}`}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export function fraseObjetivoEtapa(tipoAcao, nomeEtapa) {
  const t = String(tipoAcao || '').toUpperCase();
  const nome = nomeEtapa || 'esta etapa';
  switch (t) {
    case 'CONFIGURACAO_EDITAL':
      return `O que fazer: configurar e publicar o edital em “${nome}”.`;
    case 'MAPA_COMPARATIVO':
      return `O que fazer: revisar propostas, documentos e escolher a vencedora em “${nome}”.`;
    case 'MINUTA_CLAUSULAS':
      return `O que fazer: elaborar a minuta contratual em “${nome}”.`;
    case 'PREENCHIMENTO_FORMULARIO':
      return `O que fazer: preencher o formulário exigido em “${nome}”.`;
    case 'CONFIGURACAO_DOTACAO':
      return `O que fazer: informar a dotação orçamentária em “${nome}”.`;
    case 'ANALISE_TECNICA':
      return `O que fazer: registrar o parecer técnico em “${nome}”.`;
    case 'AUDITORIA':
      return `O que fazer: concluir a auditoria do processo em “${nome}”.`;
    case 'APROVACAO_SIMPLES':
      return `O que fazer: aprovar ou reprovar “${nome}”.`;
    case 'APROVACAO_COM_ASSINATURA':
      return `O que fazer: aprovar/reprovar com assinatura digital em “${nome}”.`;
    default:
      return `O que fazer: concluir a ação pendente em “${nome}”.`;
  }
}

export function resolverPainelEtapa(tipoAcao) {
  const t = String(tipoAcao || '').toUpperCase();
  if (t === 'CONFIGURACAO_EDITAL') return 'EDITAL';
  if (t === 'MAPA_COMPARATIVO') return 'MAPA';
  if (t === 'MINUTA_CLAUSULAS') return 'MINUTA';
  if (t === 'PREENCHIMENTO_FORMULARIO') return 'FORMULARIO';
  return 'APROVACAO';
}
